import {
  getLeoUuid,
  LeoUUID,
  getBoolean,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class UpdateLanguageConfigurationRPC implements
  LeoRPC<UpdateLanguageConfigurationRPC.Request, UpdateLanguageConfigurationRPC.Response, UpdateLanguageConfigurationRPC.Errors.Errors>
{
  abstract execute(
    request: UpdateLanguageConfigurationRPC.Request
  ): Promise<LeoRPCResult<UpdateLanguageConfigurationRPC.Response, UpdateLanguageConfigurationRPC.Errors.Errors>>;
}

export namespace UpdateLanguageConfigurationRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    languageId: string;
    isRTL: boolean;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUpdateLanguageConfigurationRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly languageId: LeoUUID;
    readonly isRTL: boolean;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    languageId: LeoUUID, 
    isRTL: boolean
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.languageId = languageId;
      this.isRTL = isRTL;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const languageId = getLeoUuid(dto, "languageId", InvalidRequestLanguageIdError);
      const isRTL = getBoolean(dto, "isRTL", InvalidRequestIsRtlError);
      return new Request(
        projectId,
        surveyId,
        languageId,
        isRTL
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          languageId: this.languageId.toDTO(),
          isRTL: this.isRTL
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `UpdateLanguageConfigurationRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',languageId='${this.languageId}',isRTL='${this.isRTL}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `UpdateLanguageConfigurationRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidLanguageId = "INVALID_LANGUAGE_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    TranslationUpdateInProgress = "TRANSLATION_UPDATE_IN_PROGRESS"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `UpdateLanguageConfigurationRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `UpdateLanguageConfigurationRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidLanguageIdDTO {
      code: string;
    }
    
    export class InvalidLanguageId extends LeoRPCError<RPCError> {
      readonly isInvalidLanguageId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidLanguageId;
        super(code);
      }
    
    
      toDTO(): InvalidLanguageIdDTO {
        const dto: InvalidLanguageIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidLanguageId.prototype.toString = function toString(): string {
      return `UpdateLanguageConfigurationRPC.INVALID_LANGUAGE_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `UpdateLanguageConfigurationRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface TranslationUpdateInProgressDTO {
      code: string;
    }
    
    export class TranslationUpdateInProgress extends LeoRPCError<RPCError> {
      readonly isTranslationUpdateInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.TranslationUpdateInProgress;
        super(code);
      }
    
    
      toDTO(): TranslationUpdateInProgressDTO {
        const dto: TranslationUpdateInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    TranslationUpdateInProgress.prototype.toString = function toString(): string {
      return `UpdateLanguageConfigurationRPC.TRANSLATION_UPDATE_IN_PROGRESS()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | InvalidLanguageId | ProjectAlreadyArchived | TranslationUpdateInProgress;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | InvalidLanguageIdDTO | ProjectAlreadyArchivedDTO | TranslationUpdateInProgressDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidLanguageId:
          return new InvalidLanguageId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.TranslationUpdateInProgress:
          return new TranslationUpdateInProgress();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends UpdateLanguageConfigurationRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends UpdateLanguageConfigurationRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestLanguageIdError extends UpdateLanguageConfigurationRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLanguageIdError";
  }
}

export class InvalidRequestIsRtlError extends UpdateLanguageConfigurationRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestIsRtlError";
  }
}


export class InvalidResponseInvalidProjectIdError extends UpdateLanguageConfigurationRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends UpdateLanguageConfigurationRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidLanguageIdError extends UpdateLanguageConfigurationRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidLanguageIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends UpdateLanguageConfigurationRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseTranslationUpdateInProgressError extends UpdateLanguageConfigurationRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTranslationUpdateInProgressError";
  }
}

